import { AuthService } from './authService';
import {
    ConsolidationAnalyzeRequest,
    ConsolidationAnalysisResponse,
    ConsolidationCapacityFeedbackRequest,
    ConsolidationCapacityFeedbackResponse,
    ConsolidationExecuteRequest,
    ConsolidationExecuteResponse,
    ConsolidationKpi,
    FreeUpCandidate
} from '../types/consolidation';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

/**
 * API-klient for konsoliderings-modulen (api/v1/consolidation).
 * Analyse/forslag her — utførelse delegeres server-side til heatmap-/plan-pipelines;
 * operatørens på-stedet-flytt går via warehouseTransferService (TRF) i v1.
 */
class ConsolidationService {
    /** Nøkkeltall for lagersjefens KPI-rad. */
    async getKpi(warehouseId: string): Promise<ConsolidationKpi> {
        const response = await AuthService.authenticatedGet(
            `${API_BASE_URL}/consolidation/kpi?warehouseId=${encodeURIComponent(warehouseId)}`
        );

        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(extractApiError(data, response, 'Kunne ikke hente KPI'));
        }

        return await response.json();
    }

    /** Kandidat-analyse (item/source/destination). */
    async analyze(request: ConsolidationAnalyzeRequest): Promise<ConsolidationAnalysisResponse> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/consolidation/analyze`,
            request
        );

        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(extractApiError(data, response, 'Analyse feilet'));
        }

        return await response.json();
    }

    /** Operatørens "Frigjør nå"-liste — nesten-tomme bins nær operatøren. */
    async getFreeUpCandidates(
        warehouseId: string,
        locationPrefix?: string,
        topN: number = 25
    ): Promise<FreeUpCandidate[]> {
        const params = new URLSearchParams();
        params.append('warehouseId', warehouseId);
        if (locationPrefix) params.append('locationPrefix', locationPrefix);
        params.append('topN', String(topN));

        const response = await AuthService.authenticatedGet(
            `${API_BASE_URL}/consolidation/free-up-candidates?${params.toString()}`
        );

        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(extractApiError(data, response, 'Kunne ikke hente frigjør-kandidater'));
        }

        return await response.json();
    }

    /** «Tøm denne lokasjonen» — alle varer i én skannet bin med foreslått destinasjon. */
    async getBinFreeCandidates(binCode: string, warehouseId: string): Promise<FreeUpCandidate[]> {
        const response = await AuthService.authenticatedGet(
            `${API_BASE_URL}/consolidation/bins/${encodeURIComponent(binCode)}/free-candidates` +
            `?warehouseId=${encodeURIComponent(warehouseId)}`
        );

        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(extractApiError(data, response, 'Kunne ikke hente innhold for lokasjonen'));
        }

        return await response.json();
    }

    /** Destinasjon-først ("Trekk inn hit") for en skannet/valgt bin. */
    async getPullCandidates(binCode: string, warehouseId: string): Promise<ConsolidationAnalysisResponse> {
        const response = await AuthService.authenticatedGet(
            `${API_BASE_URL}/consolidation/destinations/${encodeURIComponent(binCode)}/pull-candidates` +
            `?warehouseId=${encodeURIComponent(warehouseId)}`
        );

        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(extractApiError(data, response, 'Kunne ikke hente kandidater'));
        }

        return await response.json();
    }

    /** Utfør valgte linjer: immediate (StcIntRelOrder) eller plan (MoveProcess-steg). */
    async execute(request: ConsolidationExecuteRequest): Promise<ConsolidationExecuteResponse> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/consolidation/execute`,
            request
        );

        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(extractApiError(data, response, 'Utførelse feilet'));
        }

        return await response.json();
    }

    /**
     * Operatørens kapasitets-tilbakemelding: «det er plass til N stk av denne varen her».
     * Oppdaterer ItemBinSize.MaxQuantity for mål-binnens størrelse (UpdatedBy='CAPACITY-FEEDBACK')
     * og graduerer kapasiteten til observert.
     */
    async registerCapacityFeedback(
        request: ConsolidationCapacityFeedbackRequest
    ): Promise<ConsolidationCapacityFeedbackResponse> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/consolidation/capacity-feedback`,
            request
        );

        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(extractApiError(data, response, 'Kunne ikke lagre kapasitet'));
        }

        return await response.json();
    }
}

export const consolidationService = new ConsolidationService();
